import { useMemo, useRef } from "react";
import { Point } from "shared/datamodel/schemas";
import type { TemplatePreviewBoundaries } from "state-atoms/stage-atoms";
import type { BoardContext } from "elements/index";
import { noop } from "utils/fn-utils";

interface CalculatedBoundaries {
  topBoundary: number;
  bottomBoundary: number;
  availableHeight: number;
}

export function calculateTemplateBoundaries(
  boundaries: TemplatePreviewBoundaries | undefined,
  viewportHeight: number
): CalculatedBoundaries {
  let topBoundary = boundaries?.defaultTopOffset ?? 75;
  let bottomBoundary = viewportHeight - (boundaries?.defaultBottomOffset ?? 60);

  if (boundaries?.topSelector) {
    const topElement = document.querySelector(boundaries.topSelector);
    if (topElement) {
      topBoundary = topElement.getBoundingClientRect().bottom;
    }
  }

  if (boundaries?.bottomSelector) {
    const bottomElement = document.querySelector(boundaries.bottomSelector);
    if (bottomElement) {
      bottomBoundary = bottomElement.getBoundingClientRect().top;
    }
  }

  return {
    topBoundary,
    bottomBoundary,
    availableHeight: bottomBoundary - topBoundary,
  };
}

export function calculateViewportCenter(
  boundaries: CalculatedBoundaries,
  viewportWidth: number,
  posScale: { x: number; y: number; scale: number }
): Point {
  const visibleCenterX = viewportWidth / 2;
  const visibleCenterY = (boundaries.topBoundary + boundaries.bottomBoundary) / 2;

  return {
    x: (visibleCenterX - posScale.x) / posScale.scale,
    y: (visibleCenterY - posScale.y) / posScale.scale,
  };
}

/**
 * Hook to create a mock BoardContext for template ghost preview
 * Provides a complete context for rendering elements that need reflect API (e.g., Gantt charts)
 * @param elementsWithPrefix - Array of elements with "cElement-" prefix
 * @param isLoggedIn - Whether the current user is logged in
 * @returns BoardContext with mock reflect that always references current elements
 */
export function useMockBoardContext(
  elementsWithPrefix: Array<[string, unknown]>,
  isLoggedIn: boolean = false
): BoardContext {
  const elementsRef = useRef<Array<[string, unknown]>>(elementsWithPrefix);
  elementsRef.current = elementsWithPrefix;

  // Create stable reflect object only once
  const reflectRef = useRef<{
    subscribe: (query: (tx: unknown) => Promise<unknown>, callback: (data: unknown) => void) => () => void;
  }>(null as never);

  if (!reflectRef.current) {
    const activeSubscriptions = new Map<number, () => void>();
    let subscriptionIdCounter = 0;

    reflectRef.current = {
      subscribe: (query: (tx: unknown) => Promise<unknown>, callback: (data: unknown) => void) => {
        const subscriptionId = ++subscriptionIdCounter;

        const mockTx = {
          scan: () => ({
            entries: () => ({
              toArray: async () => elementsRef.current,
            }),
          }),
          get: async (key: string) => {
            const element = elementsRef.current.find(([id]) => id === key);
            return element ? element[1] : undefined;
          },
        };

        query(mockTx as never)
          .then((result) => {
            if (activeSubscriptions.has(subscriptionId) && callback && typeof callback === "function") {
              callback(result);
            }
          })
          .catch(() => {});

        const unsubscribe = () => {
          activeSubscriptions.delete(subscriptionId);
        };
        activeSubscriptions.set(subscriptionId, unsubscribe);

        return unsubscribe;
      },
    };
  }

  return useMemo(
    () => ({
      documentId: "",
      boardId: 0,
      reflect: reflectRef.current as never,
      isReadOnly: false,
      isLoggedIn,
      selectedElementIds: [],
      setSelectedIds: noop,
      setStageState: noop,
      hiddenElementIds: [],
      editingElementId: null,
      editingElementLinkId: null,
      undoRedoStack: undefined,
    }),
    [isLoggedIn]
  );
}
